import { useMemo } from "react";
import { useBusiness } from "./business";

/**
 * What the signed-in person may open in this business. Roles hold patterns: "*" (everything), "events.*",
 * "*.view". The server checks every request; this only keeps the sidebar, Settings and search to what they can use.
 */
export function can(permissions: readonly string[], wanted: string): boolean {
  for (const p of permissions) {
    if (p === "*" || p === wanted) return true;
    if (p.endsWith(".*") && wanted.startsWith(p.slice(0, -1))) return true;
    if (p.startsWith("*.") && wanted.endsWith(p.slice(1))) return true;
  }
  return false;
}

export function useAccess() {
  const { data, navItems } = useBusiness();
  return useMemo(() => access(data, navItems), [data, navItems]);
}

function access(data: ReturnType<typeof useBusiness>["data"], navItems: ReturnType<typeof useBusiness>["navItems"]) {
  const perms = data.me.permissions;
  const canDo = (need: string | readonly string[]) => (typeof need === "string" ? can(perms, need) : need.some((n) => can(perms, n)));
  return {
    owner: data.me.owner,
    administrator: perms.includes("*"),
    can: canDo,
    /** A page's address: open when the section it sits in is one this person may view. */
    canOpen: (path: string) => {
      if (path === "/" || path.startsWith("/settings") || path.startsWith("/account")) return true;
      const item = navItems.find((n) => n.to !== "/" && (path === n.to || path.startsWith(`${n.to}/`) || (n.also ?? []).some((a) => path === a || path.startsWith(`${a}/`))));
      return item ? (!item.need || canDo(item.need)) : true;
    },
  };
}
